/**
 * Board of everything flagged as a highlighted need in the source data.
 *
 * Clearing the flag — one card, or everything before a date — is a write to the
 * newsletter record, not a view-level hide: the row leaves this list and stops
 * being counted everywhere else too.
 */
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../App'
import { api, useApi, qs, fetchAllRows } from '../lib/api'
import { PartnerSelect, CountrySelect, YearSelect } from '../components/Select'
import { DateRangeField, DateField, DatePresets } from '../components/DateField'
import { TextBlock } from '../components/RichText'
import { DataTable, ViewToggle, useViewMode, SortBar } from '../components/DataTable'
import {
  Card, Field, Badge, Empty, Pager, Loading, ErrorState, SearchInput, Spinner, Modal, Notice,
  Filters, FilterActions, countFilters, useSort, fmtInt, fmtDate, relDays, PartnerLabel,
  downloadCsv, useToast, AlumniToggle, AlumniHidden,
} from '../components/ui'
import { Icon } from '../components/Icon'

const EMPTY = { q: '', partner_id: [], country_id: [], from: '', to: '', year: null, include_alumni: false }

/** Every view that counts or badges the flag, this board included. */
const STALE_ON_CHANGE = [
  'highlighted', 'newsletters', 'newsletter', 'stats-overview', 'partner', 'country',
]

export default function HighlightedNeeds({ user }) {
  const [f, setF] = useState(EMPTY)
  const [page, setPage] = useState(1)
  // Multiples of three, so the cards always end on a full row.
  const [limit, setLimit] = useState(30)
  const { sort, dir, onSort: sortBy } = useSort('published_on', 'desc', ['published_on'])
  const onSort = (key) => {
    sortBy(key)
    setPage(1)
  }
  const [busyId, setBusyId] = useState(null)
  const [clearBefore, setClearBefore] = useState(null)
  const [view, setView] = useViewMode('highlighted-needs')
  const toast = useToast()
  const qc = useQueryClient()

  const set = (patch) => {
    setF((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  // The board reports on needs still being carried, so alumni are out until asked for.
  const { include_alumni, ...filters } = f
  const params = { ...filters, include_alumni: include_alumni ? 1 : null, highlighted: 1, sort, dir }
  const query = useApi(
    ['highlighted', f, page, limit, sort, dir],
    `newsletters${qs({ ...params, page, limit })}`,
    { keepPreviousData: true }
  )

  // Every flagged update these filters match — it used to be only the page on screen.
  const exportAll = async () => {
    try {
      const all = await fetchAllRows('newsletters', params)
      downloadCsv('highlighted-updates.csv', all.rows, [
        { key: 'published_on', label: 'Date' },
        { key: 's4ca_code', label: 'S4CA Code' },
        { key: 'partner_name', label: 'Partner Unit' },
        { key: 'country_name', label: 'Country' },
        { key: 'snippet', label: 'Snippet' },
      ])
      if (all.total > all.rows.length) toast.info(`Exported the first ${fmtInt(all.rows.length)} of ${fmtInt(all.total)} rows`)
    } catch (err) {
      toast.error(err)
    }
  }

  const SORTS = [
    { key: 'published_on', label: 'Date' },
    { key: 's4ca_code', label: 'Partner Unit' },
    { key: 'country_name', label: 'Country' },
  ]

  const refreshAll = () => {
    for (const key of STALE_ON_CHANGE) qc.invalidateQueries({ queryKey: [key] })
  }

  // Clearing the flag is a write to the record, so the card leaves the board as
  // soon as the list refetches — which is the point, not a side effect.
  const unhighlight = async (row) => {
    setBusyId(row.id)
    try {
      await api(`newsletters/${row.id}`, { method: 'PUT', body: { highlighted_need: false } })
      refreshAll()
      toast.ok(`${fmtDate(row.published_on)} is no longer a highlighted need`)
    } catch (err) {
      toast.error(err)
    } finally {
      setBusyId(null)
    }
  }

  const rows = query.data?.rows ?? []
  const total = query.data?.total ?? 0

  const columns = [
    {
      key: 'published_on',
      label: 'Date',
      sortKey: 'published_on',
      width: 110,
      cellClass: 'nowrap',
      render: (r) => (
        <>
          <Link className="mono" to={`/newsletters/${r.id}`}>
            {r.published_on}
          </Link>
          <div className="subtle small">{relDays(r.published_on)}</div>
        </>
      ),
    },
    {
      key: 's4ca_code',
      label: 'Partner Unit',
      sortKey: 's4ca_code',
      width: 190,
      render: (r) => <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />,
    },
    { key: 'country_name', label: 'Country', sortKey: 'country_name', width: 140, cellClass: 'small' },
    {
      key: 'snippet',
      label: 'Snippet',
      width: '100%',
      // Clamped to two lines rather than TextBlock's pre-wrap, so a long update does not
      // stretch the row it sits in.
      cellClass: 'small cell-snippet-cell grow',
      render: (r) => <div className="cell-snippet">{r.snippet || '—'}</div>,
    },
    user.caps.update && {
      key: 'remove',
      label: '',
      width: 110,
      cellClass: 'nowrap',
      render: (r) => (
        <button
          className="btn btn-sm"
          disabled={busyId === r.id}
          title="Clear the highlighted flag on this update"
          onClick={() => unhighlight(r)}
        >
          {busyId === r.id ? <Spinner /> : <><Icon name="star" size={12} /> Remove</>}
        </button>
      ),
    },
  ].filter(Boolean)

  return (
    <PageHeader
      title="Highlighted Updates"
      sub={
        query.data ? (
          <>
            {fmtInt(total)} updates carry the highlighted flag
            <AlumniHidden count={query.data.alumniHidden} noun="updates" />
          </>
        ) : (
          'Loading…'
        )
      }
      actions={
        <>
          <ViewToggle view={view} onView={setView} />
          {user.caps.update && (
            <button className="btn btn-sm" onClick={() => setClearBefore(true)}>
              Clear before a date…
            </button>
          )}
          <button className="btn btn-sm" onClick={exportAll} disabled={!total}>
            ↓ CSV
          </button>
        </>
      }
    >
      <div className="col">
        {/* Above the panel, as on every list. */}
        <SearchInput
          id="hn-q"
          value={f.q}
          onChange={(v) => set({ q: v })}
          placeholder="Body, prayer, code or name…"
          aria-label="Search highlighted updates"
        />
        <Filters id="highlighted-needs" active={countFilters(f)} onClear={() => set(EMPTY)}>
          <Field label="Partner Unit" htmlFor="hn-partner">
            <PartnerSelect inputId="hn-partner" multi value={f.partner_id} onChange={(v) => set({ partner_id: v })} />
          </Field>
          <Field label="Country" htmlFor="hn-country">
            <CountrySelect inputId="hn-country" multi value={f.country_id} onChange={(v) => set({ country_id: v })} />
          </Field>
          <Field label="Date range">
            <DateRangeField from={f.from} to={f.to} onFrom={(v) => set({ from: v })} onTo={(v) => set({ to: v })} />
          </Field>
          <Field label="Year" htmlFor="hn-year">
            <YearSelect inputId="hn-year" value={f.year} onChange={(v) => set({ year: v })} />
          </Field>
          <AlumniToggle
            checked={f.include_alumni}
            onChange={(v) => set({ include_alumni: v })}
            where="updates"
          />
          <FilterActions active={countFilters(f)} onClear={() => set(EMPTY)} busy={query.isFetching}>
            <DatePresets onPick={(from, to) => set({ from: from ?? '', to: to ?? '' })} />
          </FilterActions>
        </Filters>

        {query.isLoading && (
          <Card>
            <Loading />
          </Card>
        )}
        {query.isError && (
          <Card>
            <ErrorState error={query.error} retry={query.refetch} />
          </Card>
        )}
        {query.data && !rows.length && (
          <Card>
            <Empty icon="star" title="Nothing flagged for these filters" />
          </Card>
        )}

        {rows.length && view === 'cards' ? <SortBar options={SORTS} sort={sort} dir={dir} onSort={onSort} /> : null}

        {rows.length ? (
          view === 'cards' ? (
            <div className="grid grid-3">
              {rows.map((r) => (
                <NeedCard
                  key={r.id}
                  r={r}
                  user={user}
                  busy={busyId === r.id}
                  onRemove={() => unhighlight(r)}
                />
              ))}
            </div>
          ) : (
            <DataTable columns={columns} rows={rows} sort={sort} dir={dir} onSort={onSort} rowTo={(r) => `/newsletters/${r.id}`} />
          )
        ) : null}

        {query.data?.total ? (
          <div className="card">
            <Pager
              page={query.data.page}
              pages={query.data.pages}
              total={query.data.total}
              limit={query.data.limit}
              onPage={setPage}
              limits={[30, 60, 120, 240]}
              onLimit={(n) => {
                setLimit(n)
                setPage(1)
              }}
            />
          </div>
        ) : null}
      </div>

      <ClearBeforeModal
        open={!!clearBefore}
        onClose={() => setClearBefore(null)}
        onCleared={refreshAll}
      />
    </PageHeader>
  )
}

/**
 * One highlighted need as a card.
 *
 * Clicking anywhere on it opens the update, the way a table row does and the way the
 * update and partner unit cards elsewhere already do. The controls inside keep their own
 * meaning — the date and the partner unit go where they say, and Remove clears the flag
 * rather than opening anything — and a click that ends a text selection is somebody
 * copying a line out of the need, not asking to navigate.
 *
 * Written as a section rather than a <Card>, since a card holding links cannot itself be
 * one: a link inside a link is not valid HTML. The date link is what keeps the card
 * reachable from the keyboard, which a clickable box on its own would not be.
 */
function NeedCard({ r, user, busy, onRemove }) {
  const navigate = useNavigate()

  const open = (e) => {
    if (e.target.closest('a,button,input,label,select')) return
    if (window.getSelection?.()?.toString()) return
    navigate(`/newsletters/${r.id}`)
  }

  return (
    <section className="card clickable" onClick={open}>
      <div className="card-body">
        <div className="row row-tight small" style={{ marginBottom: 8 }}>
          <Badge tone="warn"><Icon name="star" size={12} /> need</Badge>
          <Link className="mono" to={`/newsletters/${r.id}`}>
            {r.published_on}
          </Link>
          <span className="subtle">{relDays(r.published_on)}</span>
        </div>
        <div className="small" style={{ marginBottom: 8 }}>
          <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />
          <div className="subtle">{r.country_name}</div>
        </div>
        <div className="small muted" style={{ maxHeight: 130, overflow: 'hidden' }}>
          <TextBlock text={r.snippet} />
        </div>
        <div className="row row-tight" style={{ marginTop: 10 }}>
          <Link className="btn btn-sm" to={`/newsletters/${r.id}`}>
            Read
          </Link>
          <span className="spacer" />
          {user.caps.update && (
            <button
              className="btn btn-sm"
              disabled={busy}
              title="Clear the highlighted flag on this update"
              onClick={onRemove}
            >
              {busy ? <Spinner /> : <><Icon name="star" size={12} /> Remove</>}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * Clear the flag on everything published before a date, in one sweep.
 *
 * The count is fetched from the server before anything is written, so the
 * confirmation names a real number rather than asking the user to trust the verb.
 * There is no undo — the button says so, and the audit log records the sweep.
 */
function ClearBeforeModal({ open, onClose, onCleared }) {
  const toast = useToast()
  const [before, setBefore] = useState('')
  const [matched, setMatched] = useState(null)
  const [counting, setCounting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // Re-count whenever the date changes; a stale number here would be a lie about
  // what the button is going to do.
  useEffect(() => {
    if (!open || !before) {
      setMatched(null)
      return
    }
    let cancelled = false
    setCounting(true)
    setError(null)
    api(`newsletters/highlighted/clear${qs({ before })}`)
      .then((r) => !cancelled && setMatched(r.matched))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setCounting(false))
    return () => {
      cancelled = true
    }
  }, [open, before])

  const close = () => {
    setBefore('')
    setMatched(null)
    setError(null)
    onClose()
  }

  const run = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await api('newsletters/highlighted/clear', { method: 'POST', body: { before } })
      toast.ok(
        res.cleared
          ? `Cleared ${fmtInt(res.cleared)} highlighted need${res.cleared === 1 ? '' : 's'} from before ${fmtDate(before)}`
          : `Nothing was flagged before ${fmtDate(before)}`
      )
      onCleared?.()
      close()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title="Clear highlighted needs before a date"
      footer={
        <>
          <button
            className="btn btn-danger"
            onClick={run}
            disabled={busy || counting || !before || !matched}
          >
            {busy ? <Spinner /> : matched ? `Clear ${fmtInt(matched)} permanently` : 'Clear'}
          </button>
          <button className="btn" onClick={close} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <div className="col">
        <Field
          label="Clear everything published before"
          hint="The date itself is not included — an update published on this day keeps its flag."
          htmlFor="hn-clear-before"
        >
          <DateField
            id="hn-clear-before"
            value={before}
            onChange={(v) => setBefore(v || '')}
            isClearable
          />
        </Field>

        {before && (
          <Notice
            tone={matched ? 'warn' : 'info'}
            icon={matched ? 'warning' : 'info'}
            title={
              counting
                ? 'Counting…'
                : matched
                  ? `${fmtInt(matched)} update${matched === 1 ? '' : 's'} will lose the flag`
                  : 'Nothing matches that date'
            }
          >
            {matched ? (
              <>
                Every update published before <strong>{fmtDate(before)}</strong> will be
                removed from the highlighted list. The updates themselves are untouched — only
                the flag is cleared — but this cannot be undone from here, and it ignores the
                filters above. The change is recorded in the audit log.
              </>
            ) : (
              <>
                No update published before <strong>{fmtDate(before)}</strong> currently carries
                the highlighted flag.
              </>
            )}
          </Notice>
        )}

        {error && <Notice tone="danger" icon="warning">{error}</Notice>}
      </div>
    </Modal>
  )
}
